import type { IntegrationSummary } from '../../lib/calendar-api';
import { GmailMark } from '../email/MailSurface';
import { GmailConnectAction, type useGmailSetup } from '../email/GmailConnection';
import { CalendarMark } from './IntegrationBar';
import { Icon } from '../ui/Icon';
import styles from './HomeIntegrationControls.module.css';

export type HomeProvider = 'gmail' | 'google_calendar';

export function compactIntegrationSummary(service: IntegrationSummary | undefined, provider: HomeProvider) {
  if (!service) return 'Checking connection…';
  if (service.state === 'failed') return 'Couldn’t refresh';
  if (service.state === 'not_configured') return 'Setup unavailable';
  if (service.state === 'importing') return 'Syncing…';
  if (service.state === 'paused') return 'Paused';
  if (service.state === 'disconnecting') return 'Disconnecting…';
  if (provider === 'gmail') {
    if (service.unreadCount === undefined || service.scheduledTodayCount === undefined) return 'Checking email counts…';
    return `${service.unreadCount} unread · ${service.scheduledTodayCount} scheduled today`;
  }
  const today = service.todayCount ?? 0;
  const count = today || service.totalCount || 0;
  return `${count} meeting${count === 1 ? '' : 's'} ${today ? 'today' : 'this week'}`;
}

export function HomeIntegrationControls({ gmail, calendar, activeProvider, onSelect, placement, panelId, gmailSetup, onCalendarConnect, calendarConnecting }: {
  gmail: IntegrationSummary | undefined; calendar: IntegrationSummary | undefined;
  activeProvider: HomeProvider | null; onSelect: (provider: HomeProvider | null) => void;
  placement: 'header' | 'intro'; panelId: string;
  gmailSetup: ReturnType<typeof useGmailSetup>;
  onCalendarConnect: () => void; calendarConnecting: boolean;
}) {
  return <div className={styles.controls} data-placement={placement} role="group" aria-label="Home integrations">
    {(['gmail', 'google_calendar'] as const).map(provider => {
      const service = provider === 'gmail' ? gmail : calendar;
      const name = provider === 'gmail' ? 'Gmail' : 'Google Calendar';
      const setup = service && ['disconnected', 'reconnect_required', 'not_configured'].includes(service.state);
      const title = setup ? `${service.state === 'reconnect_required' ? 'Reconnect' : 'Connect'} ${name}` : name;
      const triggerLabel = placement === 'intro' && setup
        ? `${name} ${service.state === 'reconnect_required' ? 'needs reconnecting' : 'not connected'}` : title;
      const summary = compactIntegrationSummary(service, provider);
      const expanded = activeProvider === provider;
      if (expanded && setup) return <div key={provider} className={styles.openControl}>
        {provider === 'gmail' ? <GmailConnectAction compact connection={gmailSetup.connect} />
          : <button type="button" className={styles.connect} onClick={onCalendarConnect}
            disabled={calendarConnecting || service.canConnect === false || service.state === 'not_configured'}>
            <CalendarMark />{calendarConnecting ? 'Opening Google…' : title}
          </button>}
        <button type="button" className={styles.dismiss} onClick={() => onSelect(null)}>Dismiss</button>
      </div>;
      return <button key={provider} type="button" className={styles.control} aria-expanded={expanded} aria-controls={panelId}
        aria-label={setup ? triggerLabel : `${name}: ${summary}`} onClick={() => onSelect(expanded ? null : provider)}>
        {provider === 'gmail' ? <GmailMark /> : <CalendarMark />}
        <span className={styles.label}><strong>{triggerLabel}</strong>{!setup && <span className={styles.summary}>{summary}</span>}</span>
        <Icon name="chevron-down" size={12} />
      </button>;
    })}
  </div>;
}
